import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LoggingUserProps } from "../../types/user";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";

export interface LoggingUserState {
  user: LoggingUserProps;
  loading: boolean;
  error: string | null;
}

const initialState: LoggingUserState = {
  user: {} as LoggingUserProps,
  loading: false,
  error: null,
};

export const fetchLoggingUserData = createAsyncThunk(
  "fetchLoggingUserData",
  async (_, { rejectWithValue }) => {
    try {
      const response: any = await GET(`/v1/logging-user`, {});
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data ?? err?.message ?? "Request failed"
      );
    }
  }
);

export const loggingUserSlice = createSlice({
  name: "loggingUser",
  initialState,
  reducers: {
    setPermissionsFromLogin: (state, action) => {
      if (state.user) {
        state.user.permissions = action.payload.permissions;
        state.user.roles = action.payload.roles;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchLoggingUserData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchLoggingUserData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.user = action.payload.data.data;
        state.error = null;
      } else {
        state.user = {} as LoggingUserProps;
        state.error =
          action?.payload?.data?.message ||
          "Something went wrong. Please try again.";
      }
    });
    builder.addCase(fetchLoggingUserData.rejected, (state, action: any) => {
      state.loading = false;
      state.error =
        action?.payload?.message ||
        "Something went wrong. Please try again.";
    });
  },
});

export const { setPermissionsFromLogin } = loggingUserSlice.actions;

export const getLoggingUser = (state: RootState) => state.loggingUser.user;

export const getCurrentBranchPermissions = (state: RootState): string[] => {
  const user = state.loggingUser.user;
  const branch = state.settings.selectedBranch;
  if (!user?.permissions || !branch) return [];
  return user.permissions[String(branch.id)] ?? [];
};

export const getIsSuperAdmin = (state: RootState): boolean => {
  const user = state.loggingUser.user;
  if (!user?.roles) return false;
  return Object.values(user.roles).some((r) => r.includes("Super Admin"));
};

export default loggingUserSlice.reducer;
